/** Dados e ações da venda pelo chat (Prompt 06). */
import type { OpcoesVenda, ProdutoErp, RascunhoVenda, Venda } from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ErroApi } from "./api";

export const useVendas = (conversaId: string) =>
  useQuery({
    queryKey: ["conversa", conversaId, "vendas"],
    queryFn: () =>
      api.get<{ carrinho: Venda | null; pedidos: Venda[] }>(`/conversas/${conversaId}/vendas`),
  });

export const useOpcoesVenda = (conversaId: string, habilitado: boolean) =>
  useQuery({
    queryKey: ["opcoes-venda", conversaId],
    queryFn: () => api.get<OpcoesVenda>(`/conversas/${conversaId}/vendas/opcoes`),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });

export interface FiltroProduto {
  texto: string;
  tamanho: string;
  cor: string;
  somenteComEstoque: boolean;
}

export const useProdutos = (conversaId: string, f: FiltroProduto) =>
  useQuery({
    queryKey: ["produtos-erp", conversaId, f],
    queryFn: () => {
      const q = new URLSearchParams();
      // Só números/letras com hífen e sem espaço: provavelmente SKU ou código.
      if (/^[\w-]{3,}$/.test(f.texto) && /\d/.test(f.texto)) q.set("sku", f.texto);
      else if (f.texto) q.set("texto", f.texto);
      if (f.tamanho) q.set("tamanho", f.tamanho);
      if (f.cor) q.set("cor", f.cor);
      if (f.somenteComEstoque) q.set("somenteComEstoque", "true");
      return api.get<ProdutoErp[]>(`/conversas/${conversaId}/vendas/produtos?${q}`);
    },
    enabled: f.texto.trim().length >= 2 || !!f.tamanho || !!f.cor,
    staleTime: 30_000,
  });

export function useAcoesVenda(conversaId: string) {
  const qc = useQueryClient();
  const chave = ["conversa", conversaId, "vendas"];
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: chave });
    void qc.invalidateQueries({ queryKey: ["conversa", conversaId, "dados"] });
    void qc.invalidateQueries({ queryKey: ["conversa", conversaId, "mensagens"] });
    void qc.invalidateQueries({ queryKey: ["conversas"] });
  };
  const salvar = useMutation({
    mutationFn: (r: RascunhoVenda) => api.put<Venda>(`/conversas/${conversaId}/vendas/carrinho`, r),
    onSuccess: (v) => {
      qc.setQueryData<{ carrinho: Venda | null; pedidos: Venda[] }>(chave, (atual) => ({
        pedidos: atual?.pedidos ?? [],
        carrinho: v,
      }));
      void qc.invalidateQueries({ queryKey: ["conversas"] });
    },
  });
  const confirmar = useMutation({
    mutationFn: () => api.post<Venda>(`/conversas/${conversaId}/vendas/confirmar`),
    onSuccess: (v) => {
      toast.success(`Pedido #${v.pedido?.numero ?? ""} criado no ERP!`);
      invalidar();
    },
    onError: (e) => {
      const msg = e instanceof ErroApi ? e.message : "Não foi possível criar o pedido.";
      if (e instanceof ErroApi && e.codigo === "pedido_pendente") toast.message(msg);
      else toast.error(msg);
      invalidar();
    },
  });
  const descartar = useMutation({
    mutationFn: () => api.del(`/conversas/${conversaId}/vendas/carrinho`),
    onSuccess: invalidar,
    onError: (e) => toast.error(e instanceof ErroApi ? e.message : "Não foi possível descartar."),
  });
  return { salvar, confirmar, descartar };
}
